/** 12 fixed ink colours with ≥ 4.5:1 contrast on white (decision I8). */
export const INK_COLORS: readonly string[] = [
  '#1a1a1a',
  '#5c5c5c',
  '#c92a2a',
  '#d9480f',
  '#a15c00',
  '#2b8a3e',
  '#0b7285',
  '#1864ab',
  '#4c3fc7',
  '#9c36b5',
  '#c2255c',
  '#7c4a1e',
];

/** Separate highlighter palette, ordered by hue like every palette: pink, orange, yellow, green, blue. */
export const HIGHLIGHTER_COLORS: readonly string[] = [
  '#faa2c1',
  '#ffc078',
  '#ffe066',
  '#8ce99a',
  '#74c0fc',
];

/** The highlighter starts yellow (classic marker), wherever it sits in the palette. */
export const DEFAULT_HIGHLIGHTER_COLOR = '#ffe066';

export const RECENT_COLORS_MAX = 5;

/** `#rgb`, `rgb`, `#rrggbb` or `rrggbb` → lower-case `#rrggbb`; null otherwise. */
export function normalizeHexColor(input: string): string | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(input.trim());
  if (!m) return null;
  const hex = m[1]!.toLowerCase();
  return `#${hex.length === 3 ? [...hex].map((c) => c + c).join('') : hex}`;
}

/** Most recent first, without duplicates, at most `max` entries. */
export function pushRecentColor(
  recent: readonly string[],
  color: string,
  max = RECENT_COLORS_MAX,
): string[] {
  const c = color.toLowerCase();
  return [c, ...recent.filter((r) => r.toLowerCase() !== c)].slice(0, max);
}

function channels(color: string): [number, number, number] {
  const n = Number.parseInt(color.slice(1), 16);
  return [Math.floor(n / 65536), Math.floor(n / 256) % 256, n % 256];
}

/** Black and dark greys (low saturation, low luminance). */
export function isDarkNeutral(color: string): boolean {
  const [r, g, b] = channels(color);
  const spread = Math.max(r, g, b) - Math.min(r, g, b);
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return spread <= 40 && luminance < 0.4;
}

/**
 * Display colour of ink: in dark mode black / dark grey is inverted so it stays readable
 * (decision I8). Stored colours and the PDF keep the original.
 */
export function displayInkColor(color: string, theme: 'light' | 'dark'): string {
  if (theme === 'light' || !isDarkNeutral(color)) return color;
  return `#${channels(color)
    .map((c) => (255 - c).toString(16).padStart(2, '0'))
    .join('')}`;
}
